import React, { useState } from 'react';
import axios from 'axios';
import { QuestionMarkCircleIcon, SparklesIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { DIRECTUS_ARTICLES_ENDPOINT, LOCAL_WORKER_API_BASE } from '../../../api';

// Spinner component
const Spinner = ({ className }) => (
  <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);

// Helper to determine if we're on localhost
const isLocalhost = () => {
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
};

const sanitizeFaqQuestion = (value) => {
  const text = String(value || '')
    .replace(/\*\*/g, '')
    .replace(/【.*?】/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\?+$/g, '');
  if (!text) return '';
  return `${text}?`;
};

const sanitizeFaqAnswer = (value) => {
  const text = String(value || '')
    .replace(/【.*?】/g, '')
    .replace(/\*\*(.*?)\*\*/g, '<b>$1</b>')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) return '';
  return text.startsWith('<p>') ? text : `<p>${text}</p>`;
};

const normalizeFaqList = (items) => {
  return (Array.isArray(items) ? items : [])
    .map((item) => ({
      question: sanitizeFaqQuestion(item?.question ?? item?.q ?? ''),
      answer: sanitizeFaqAnswer(item?.answer ?? item?.a ?? '')
    }))
    .filter((item) => item.question && item.answer);
};

const tryParseFaqJson = (content) => {
  const raw = String(content || '').trim();
  if (!raw) return null;

  const candidates = [];
  const fenced = raw.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
  if (fenced?.[1]) candidates.push(fenced[1].trim());
  candidates.push(raw);

  const firstBrace = raw.indexOf('{');
  const lastBrace = raw.lastIndexOf('}');
  if (firstBrace >= 0 && lastBrace > firstBrace) {
    candidates.push(raw.slice(firstBrace, lastBrace + 1));
  }

  const firstBracket = raw.indexOf('[');
  const lastBracket = raw.lastIndexOf(']');
  if (firstBracket >= 0 && lastBracket > firstBracket) {
    candidates.push(raw.slice(firstBracket, lastBracket + 1));
  }

  for (const candidate of candidates) {
    try {
      const parsed = JSON.parse(candidate);
      if (Array.isArray(parsed)) {
        const faq = normalizeFaqList(parsed);
        if (faq.length) return { faq };
      }
      if (Array.isArray(parsed?.faq)) {
        const faq = normalizeFaqList(parsed.faq);
        if (faq.length) return { faq };
      }
    } catch (_error) {
      // Continue with next candidate.
    }
  }

  return null;
};

// Helper to parse markdown/plain FAQ format to JSON
const parseMarkdownFAQ = (markdownContent) => {
  const lines = String(markdownContent || '').split('\n');
  const faqItems = [];
  let currentQuestion = '';
  let currentAnswer = [];

  const flushCurrentItem = () => {
    const question = sanitizeFaqQuestion(currentQuestion);
    const answer = sanitizeFaqAnswer(currentAnswer.join(' '));
    if (question && answer) {
      faqItems.push({ question, answer });
    }
  };

  for (const line of lines) {
    const trimmedLine = line.trim();
    if (!trimmedLine) continue;

    const numberedBold = trimmedLine.match(/^\d+[.)]\s*\*\*(.+?)\*\*\??$/);
    const numberedPlain = trimmedLine.match(/^\d+[.)]\s*(.+?\?)\s*$/);
    const numberedAny = trimmedLine.match(/^\d+[.)]\s*(.+)$/);
    const qaQuestion = trimmedLine.match(/^(?:Q|Pytanie)\s*[:-]\s*(.+)$/i);

    const questionValue = numberedBold?.[1] || numberedPlain?.[1] || qaQuestion?.[1] || numberedAny?.[1] || null;

    if (questionValue) {
      flushCurrentItem();
      currentQuestion = questionValue;
      currentAnswer = [];
      continue;
    }

    if (currentQuestion) {
      currentAnswer.push(trimmedLine.replace(/^[-*]\s+/, ''));
    }
  }

  flushCurrentItem();
  return { faq: faqItems };
};

const parseFAQResponse = (generatedContent) => {
  const fromJson = tryParseFaqJson(generatedContent);
  if (fromJson?.faq?.length) return fromJson;

  const fromMarkdown = parseMarkdownFAQ(generatedContent);
  if (fromMarkdown?.faq?.length) return fromMarkdown;

  throw new Error('FAQ response format not recognized or empty');
};

const FAQTab = ({ faqItems, setFaqItems, generateFAQ, handleSave }) => {
  const { t } = useTranslation();
  const [isGenerating, setIsGenerating] = useState(false);
  // Removed editingItems - no longer used

  const handleGenerateFAQ = async () => {
    try {
      setIsGenerating(true);
      
      // Get thread_id from current article
      const articleId = window.location.pathname.split('/').pop();
      const token = localStorage.getItem('directus_token');
      
      console.log('Starting FAQ generation for article ID:', articleId);
      
      const articleResponse = await axios.get(`${DIRECTUS_ARTICLES_ENDPOINT}/${articleId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const threadId = articleResponse.data.data.thread_id;
      const title = articleResponse.data.data.title;
      const language = articleResponse.data.data.language || 'pl';
      
      // Get article content and ensure it's a string
      let content = '';
      const articleBody = articleResponse.data.data.article_body;
      
      if (articleBody) {
        if (typeof articleBody === 'string') {
          content = articleBody;
        } else if (typeof articleBody === 'object') {
          // If it's an object, try to get text from content or convert to JSON string
          content = articleBody.content || JSON.stringify(articleBody);
        }
      }
      
      if (!threadId) {
        alert('No thread_id found for this article. Please generate the article first.');
        return;
      }
      
      console.log('Article:', {
        id: articleId,
        title,
        threadId,
        language,
        contentType: typeof content,
        contentLength: content.length
      });
      
      // Determine API URL based on environment
      const apiUrl = isLocalhost() 
        ? `${LOCAL_WORKER_API_BASE}/faq`
        : 'https://api.prosecaptain.com/workers/api/faq';
      
      console.log('Calling FAQ API:', apiUrl);
      
      try {
        // Call backend FAQ endpoint
        // The worker reads the article's conversation server-side from articleId.
        const faqResponse = await axios.post(apiUrl, {
          articleId: Number(articleId),
          articleContent: `Title: ${title}\n\nContent: ${content}`,
          language: language
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (!faqResponse.data.success) {
          throw new Error(faqResponse.data.error || 'FAQ generation failed');
        }
        
        const generatedContent = faqResponse.data.faq;
        console.log('Generated content:', generatedContent);
        
        try {
          const parsedFAQ = parseFAQResponse(generatedContent);
          if (parsedFAQ.faq && Array.isArray(parsedFAQ.faq)) {
            console.log('FAQ parsed successfully:', parsedFAQ.faq);
            setFaqItems(parsedFAQ.faq);
            handleSave({ faq: parsedFAQ.faq });
            
            // Save data in Directus via direct endpoint
            try {
              console.log('Saving FAQ data in Directus via direct endpoint...');
              const directusResponse = await axios.patch(
                `${DIRECTUS_ARTICLES_ENDPOINT}/${articleId}`,
                {
                  faq: JSON.stringify(parsedFAQ.faq)
                },
                {
                  headers: { Authorization: `Bearer ${token}` }
                }
              );
              console.log('FAQ data saved in Directus:', directusResponse.data);
            } catch (directusError) {
              console.error('Error saving FAQ data in Directus:', directusError);
              // Don't stop the process if additional save fails
            }
          } else {
            throw new Error('Invalid FAQ format in response');
          }
        } catch (parseError) {
          console.error('Error parsing FAQ response:', parseError);
          throw new Error('Failed to parse FAQ response: ' + parseError.message);
        }
        
      } catch (innerError) {
        console.error('Error during FAQ generation:', innerError);
        throw innerError;
      }
      
    } catch (error) {
      console.error('Error generating FAQ:', error);
      alert(`Error occurred while generating FAQ: ${error.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const updateFAQItem = (index, field, value) => {
    const updatedFaq = [...faqItems];
    updatedFaq[index] = { ...updatedFaq[index], [field]: value };
    setFaqItems(updatedFaq);
  };

  const saveFAQItem = async (index) => {
    try {
      const articleId = window.location.pathname.split('/').pop();
      const token = localStorage.getItem('directus_token');
      
      await axios.patch(
        `${DIRECTUS_ARTICLES_ENDPOINT}/${articleId}`,
        {
          faq: JSON.stringify(faqItems)
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      console.log('FAQ item saved successfully');
    } catch (error) {
      console.error('Error saving FAQ item:', error);
    }
  };

  const addNewFAQItem = () => {
    const newFaqItem = { question: '', answer: '' };
    const updatedFaq = [...faqItems, newFaqItem];
    setFaqItems(updatedFaq);
  };

  const deleteFAQItem = async (index) => {
    const updatedFaq = [...faqItems];
    updatedFaq.splice(index, 1);
    setFaqItems(updatedFaq);
    
    // Save immediately after deletion
    try {
      const articleId = window.location.pathname.split('/').pop();
      const token = localStorage.getItem('directus_token');
      
      await axios.patch(
        `${DIRECTUS_ARTICLES_ENDPOINT}/${articleId}`,
        {
          faq: JSON.stringify(updatedFaq)
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
    } catch (error) {
      console.error('Error saving FAQ after deletion:', error);
    }
  };

  return (
    <div>
      {faqItems && faqItems.length > 0 ? (
        <div className="space-y-4">
          {faqItems.map((item, index) => (
            <div key={index} className="p-4 border border-gray-200 rounded-lg bg-white">
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Question
                  </label>
                  <input
                    type="text"
                    value={item.question || ''}
                    onChange={(e) => updateFAQItem(index, 'question', e.target.value)}
                    onBlur={() => saveFAQItem(index)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter your question here..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Answer
                  </label>
                  <textarea
                    value={item.answer ? item.answer.replace(/<[^>]*>/g, '') : ''}
                    onChange={(e) => updateFAQItem(index, 'answer', `<p>${e.target.value}</p>`)}
                    onBlur={() => saveFAQItem(index)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter your answer here..."
                  />
                </div>
              </div>
              
              <div className="flex justify-end mt-3">
                <button 
                  className="text-sm text-red-600 hover:text-red-800 px-3 py-1 rounded-md hover:bg-red-50 transition-colors"
                  onClick={() => deleteFAQItem(index)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
          
          <div className="mt-6 pt-4 border-t border-gray-200 space-y-3">
            <button 
              className="w-full text-sm text-primary-500 hover:text-primary-600 py-2 px-4 border border-primary-200 rounded-md hover:bg-primary-50 transition-colors"
              onClick={addNewFAQItem}
            >
              + {t('articles.editor.faq.actions.addNewItem')}
            </button>
            
            <button 
              className="w-full text-sm bg-primary-500 text-white hover:bg-primary-600 py-2 px-4 rounded-md transition-colors"
              onClick={handleGenerateFAQ}
              disabled={isGenerating}
            >
              {isGenerating
                ? t('articles.editor.faq.actions.regeneratingWithAi')
                : t('articles.editor.faq.actions.generateWithAi')}
            </button>
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-6">
            <QuestionMarkCircleIcon className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Create FAQ Section</h3>
          <p className="text-gray-500 mb-8 max-w-md mx-auto">
            Help your readers by answering common questions. Add questions manually or let AI generate them based on your content.
          </p>
          
          <div className="space-y-3 max-w-sm mx-auto">
            <button 
              className={`w-full flex items-center justify-center gap-2 text-sm py-3 px-4 rounded-lg transition-colors ${
                isGenerating 
                  ? 'bg-primary-400 cursor-not-allowed' 
                  : 'bg-primary-500 text-white hover:bg-primary-600'
              }`}
              onClick={handleGenerateFAQ}
              disabled={isGenerating}
            >
              {isGenerating ? (
                <Spinner className="w-4 h-4 text-white" />
              ) : (
                <SparklesIcon className="w-4 h-4" />
              )}
              <span className={isGenerating ? "animate-pulse bg-gradient-to-r from-white to-gray-200 bg-clip-text text-transparent" : "text-white"}>
                {isGenerating ? "Generating with AI..." : "Generate with AI"}
              </span>
            </button>
            
            <button 
              className={`w-full flex items-center justify-center gap-2 text-sm py-3 px-4 border rounded-lg transition-colors ${
                isGenerating 
                  ? 'text-gray-400 border-gray-200 cursor-not-allowed bg-gray-50' 
                  : 'text-primary-600 hover:text-primary-700 border-primary-300 hover:bg-primary-50'
              }`}
              onClick={addNewFAQItem}
              disabled={isGenerating}
            >
              <PlusIcon className="w-4 h-4" />
              Add Question Manually
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FAQTab; 